// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMFilterBuilderComponent } from '@modal/filter-builder/lm-filter-builder.component';
import { Observable, Subject } from 'rxjs';
import { AppConstants, FilterBuilderDialogResult } from '@utils/index';
import { LMFormatUtilService } from '@services/utils';
import { LMModalWrapperService } from '@app/services';

@Injectable()
export class LMFilterBuilderService {
  constructor(
    private lmFormatUtilService: LMFormatUtilService,
    private lmModalWrapperService: LMModalWrapperService,
  ) { }

  public openBuilderDialog(options: LMFilterBuilderOptions): Observable<LMFilterBuilderResult> {
    let lmFilterBuilderComponentRef: LMFilterBuilderComponent;
    const returnQuery = new Subject<LMFilterBuilderResult>();

    const closeModalWith = (result, modal) => {
      if (result === FilterBuilderDialogResult.CONFIRMATION_CANCEL) {
        modal.close(true);
        returnQuery.next({ dialogResult: result, lplQuery: undefined, matchOption: lmFilterBuilderComponentRef.matchOption });
        returnQuery.complete();
        return;
      }

      lmFilterBuilderComponentRef.validateFilterConditions().subscribe(valid => {

        if (valid) {
          const lplQuery = this.convertFilterRowCriteria(lmFilterBuilderComponentRef.filterRows, lmFilterBuilderComponentRef.matchOption);
          modal.close(true);
          returnQuery.next({ dialogResult: result, lplQuery, matchOption: lmFilterBuilderComponentRef.matchOption, conditionTerms: lmFilterBuilderComponentRef.conditionTerms});
          returnQuery.complete();
        }
      });
    };

    // Create modal button
    const buttons = [
      {
        text: Soho.Locale.translate('Cancel'),
        click: (e, modal) => {
          closeModalWith(FilterBuilderDialogResult.CONFIRMATION_CANCEL, modal);
        },
        isDefault: false
      }
    ];

    if (options.hasExistingCondition) {
      buttons.push(
        {
          text: Soho.Locale.translate('Append'),
          click: (e, modal) => {
            closeModalWith(FilterBuilderDialogResult.CONFIRMATION_APPEND, modal);
          },
          isDefault: true
        },
        {
          text: Soho.Locale.translate('Overwrite'),
          click: (e, modal) => {
            closeModalWith(FilterBuilderDialogResult.CONFIRMATION_OVERWRITE, modal);
          },
          isDefault: false
        }
      );
    } else {
      buttons.push(
        {
          text: Soho.Locale.translate('Ok'),
          click: (e, modal) => {
            closeModalWith(FilterBuilderDialogResult.CONFIRMATION_OK, modal);
          },
          isDefault: true
        }
      );
    }

    const attributes: SohoAttributesArray = [ { name: 'data-automation-id', value: 'lm-filter-builder-modal' } ];

    this.lmModalWrapperService
      .modal(LMFilterBuilderComponent, <SohoModalOptions>{ attributes })
      .options({ fullsize: 'responsive' })
      .cssClass('lm-filter-builder')
      .title(this.lmFormatUtilService.formatMessage(Soho.Locale.translate('LplConditionBuilderTitle'), options.businessClass))
      .buttons(buttons)
      .open()
      .apply((dialogComponent: LMFilterBuilderComponent) => {
        lmFilterBuilderComponentRef = dialogComponent;
        lmFilterBuilderComponentRef.options = options;
      });

    return returnQuery.asObservable();
  }

  public convertFilterRowCriteria(filterRows: Array<FilterRow>, matchOption: string): string {
    const sb: Array<string> = [];

    filterRows.forEach(filterRow => {
      let rowCondition = '';

      // if true or false condition (must be boolean or condition) has no predicate
      if (filterRow.condition === 'true') {
        rowCondition += filterRow.subject.text;
      } else if (filterRow.condition === 'false') {
        rowCondition += '!' + filterRow.subject.text;
      } else {
        switch (filterRow.option) {
          case 'field':
            const field = this.getPredicateField(filterRow);
            rowCondition += this.buildPredicateCondition(filterRow, field);
            break;
          case 'custom':
            const exactValue = this.getPredicateValue(filterRow);
            rowCondition += this.buildPredicateCondition(filterRow, exactValue);
            break;
          case 'today':
            rowCondition += this.buildTodayCondition(filterRow);
            break;
          case 'yesterday':
            rowCondition += this.buildYesterdayCondition(filterRow);
            break;
          case 'tomorrow':
            rowCondition += this.buildTomorrowCondition(filterRow);
            break;
          case 'thisMonth':
            rowCondition += this.buildThisMonthCondition(filterRow);
            break;
          case 'lastMonth':
            rowCondition += this.buildLastMonthCondition(filterRow);
            break;
          case 'nextMonth':
            rowCondition += this.buildNextMonthCondition(filterRow);
            break;
          case 'thisYear':
            rowCondition += this.buildThisYearCondition(filterRow);
            break;
          case 'lastYear':
            rowCondition += this.buildLastYearCondition(filterRow);
            break;
          case 'nextYear':
            rowCondition += this.buildNextYearCondition(filterRow);
            break;
          case 'yearToDate':
            rowCondition += this.buildYearToDateCondition(filterRow);
            break;
          case 'monthToDate':
            rowCondition += this.buildMonthToDateCondition(filterRow);
            break;
          case 'actor':
          case 'authenticated actor':
            rowCondition += this.buildKeyFieldAgentCondition(filterRow);
        }
      }

      if (rowCondition.trim().length > 0) {
        sb.push('(' + rowCondition + ')');
      }
    });

    let instanceSelection = '';
    if (sb.length > 0 ) {
      sb.forEach((s, i) => {
        instanceSelection += `${s}`;
        if (sb.length > 1 && (i + 1) !== sb.length ) {
          instanceSelection += ` ${matchOption} `;
        }
      });
      return instanceSelection;
    } else {
      return instanceSelection;
    }
 }
  private getPredicateField(filterRow: FilterRow) {
    const predicate = `${filterRow.predicate.text}`;
    return predicate.trim();
  }
  private getPredicateValue(filterRow: FilterRow): string {
    let predicate = `${filterRow.predicate.text}`;
    predicate =  predicate.trim();

    switch (filterRow.subject.fieldType) {
      case AppConstants.TIMESTAMP:
      case AppConstants.YYYY:
      case AppConstants.YYYYMM:
      case AppConstants.YYYYMMDD:
      case AppConstants.MMDD:
        const dateValue =  this.lmFormatUtilService.toDataValue(predicate, filterRow.subject.fieldType);
        return `"${dateValue}"`;
    }

    if (filterRow.subject.type !== 'NUMBER' || filterRow.subject.isDimension) {
      return `"${predicate}"`;
    } else {
      return predicate;
    }
  }

  public localizePredicateValue(filterRow: FilterRow) {
    const predicate =  filterRow.predicate.text?.trim();

    if (predicate?.length > 0) {
      switch (filterRow.subject.fieldType) {
        case AppConstants.TIMESTAMP:
        case AppConstants.YYYY:
        case AppConstants.YYYYMM:
        case AppConstants.YYYYMMDD:
        case AppConstants.MMDD:
          filterRow.predicate.text = this.lmFormatUtilService.toDisplayValue(predicate, filterRow.subject.fieldType);
      }
    }
  }
  private buildPredicateCondition(filterRow: FilterRow, predicate: string): string {

   switch (filterRow.condition) {
     case 'equalTo':
       return filterRow.subject.text  + ' = '   + predicate;
     case 'notEqualTo':
       return filterRow.subject.text  + ' != '  + predicate;
     case 'greaterThan':
       return filterRow.subject.text  + ' > '   + predicate;
     case 'greaterThanOrEqualTo':
       return filterRow.subject.text  + ' >= '  + predicate;
     case 'lessThan':
       return filterRow.subject.text  + ' < '   + predicate;
     case 'lessThanOrEqualTo':
       return filterRow.subject.text  + ' <= '  + predicate;
     case 'like':
       return filterRow.subject.text  + ' like ' + predicate;
     case 'within':
       return filterRow.subject.text + ' within ' + predicate;
     case 'entered':
       return filterRow.subject.text  + ' entered';
     case 'notEntered':
       return filterRow.subject.text  + ' not entered';
     case 'exist':
       return filterRow.subject.text  + ' exists';
     case 'doesNotExist':
       return filterRow.subject.text  + ' not exists';

     default:
       return '';
   }
 }
  private buildTodayCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      const today =  'current corporate date';
      switch (filterRow.condition) {
        case 'equalTo':
          return filterRow.subject.text + ' = '   + today;
        case 'notEqualTo':
          return filterRow.subject.text + ' != '  + today;
        case 'greaterThan':
          return filterRow.subject.text + ' > '   + today;
        case 'greaterThanOrEqualTo':
          return filterRow.subject.text + ' >= '  + today;
        case 'lessThan':
          return filterRow.subject.text + ' < '   + today;
        case 'lessThanOrEqualTo':
          return filterRow.subject.text + ' <= '  + today;
        default:
          return '';
      }
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
      switch (filterRow.condition) {
        case 'equalTo':
          return filterRow.subject.text + ' corporate date = current corporate date';
        case 'notEqualTo':
          return filterRow.subject.text + ' corporate date != current corporate date';
        case 'greaterThan':
          return filterRow.subject.text + ' corporate date > current corporate date';
        case 'greaterThanOrEqualTo':
          return filterRow.subject.text + ' corporate date >= current corporate date';
        case 'lessThan':
          return filterRow.subject.text + ' corporate date < current corporate date';
        case 'lessThanOrEqualTo':
          return filterRow.subject.text + ' corporate date <= current corporate date';
        default:
          return '';
      }
    }
  }
  private buildYesterdayCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      const yesterday =  'current corporate date - 1';
      switch (filterRow.condition) {
        case 'equalTo':
          return filterRow.subject.text + ' = ' + yesterday;
        case 'notEqualTo':
          return filterRow.subject.text + ' != ' + yesterday;
        case 'greaterThan':
          return filterRow.subject.text + ' > ' + yesterday;
        case 'greaterThanOrEqualTo':
          return filterRow.subject.text + ' >= ' + yesterday;
        case 'lessThan':
          return filterRow.subject.text + ' < ' + yesterday;
        case 'lessThanOrEqualTo':
          return filterRow.subject.text + ' <= ' + yesterday;
        default:
          return '';
      }
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
      switch (filterRow.condition) {
        case 'equalTo':
          return filterRow.subject.text + ' corporate date = current corporate date - 1';
        case 'notEqualTo':
          return filterRow.subject.text + ' corporate date != current corporate date - 1';
        case 'greaterThan':
          return filterRow.subject.text + ' corporate date > current corporate date - 1';
        case 'greaterThanOrEqualTo':
          return filterRow.subject.text + ' corporate date >= current corporate date - 1';
        case 'lessThan':
          return filterRow.subject.text + ' corporate date < current corporate date - 1';
        case 'lessThanOrEqualTo':
          return filterRow.subject.text + ' corporate date <= current corporate date - 1';
        default:
          return '';
      }
    }
  }
  private buildTomorrowCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      const tomorrow =  'current corporate date + 1';
      switch (filterRow.condition) {
        case 'equalTo':
          return filterRow.subject.text + ' = ' + tomorrow;
        case 'notEqualTo':
          return filterRow.subject.text + ' != ' + tomorrow;
        case 'greaterThan':
          return filterRow.subject.text + ' > ' + tomorrow;
        case 'greaterThanOrEqualTo':
          return filterRow.subject.text + ' >= ' + tomorrow;
        case 'lessThan':
          return filterRow.subject.text + ' < ' + tomorrow;
        case 'lessThanOrEqualTo':
          return filterRow.subject.text + ' <= ' + tomorrow;
        default:
          return '';
      }
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
      switch (filterRow.condition) {
        case 'equalTo':
          return filterRow.subject.text + ' corporate date = current corporate date + 1';
        case 'notEqualTo':
          return filterRow.subject.text + ' corporate date != current corporate date + 1';
        case 'greaterThan':
          return filterRow.subject.text + ' corporate date > current corporate date + 1';
        case 'greaterThanOrEqualTo':
          return filterRow.subject.text + ' corporate date >= current corporate date + 1';
        case 'lessThan':
          return filterRow.subject.text + ' corporate date < current corporate date + 1';
        case 'lessThanOrEqualTo':
          return filterRow.subject.text + ' corporate date <= current corporate date + 1';
        default:
          return '';
      }
    }

  }
  private buildThisMonthCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      return filterRow.subject.text +  ' within current corporate period';
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
    return filterRow.subject.text + ' corporate date within current corporate period'; // Conditions should have a within
    }
  }
  private buildNextMonthCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      return filterRow.subject.text + ' within current corporate period + 1';
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
    return filterRow.subject.text + ' corporate date within current corporate period + 1';
    }
  }
  private buildLastMonthCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      return filterRow.subject.text + ' within current corporate period - 1';
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
      return filterRow.subject.text + ' corporate date within current corporate period - 1';
    }
 }
  private buildThisYearCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      return filterRow.subject.text + ' within current corporate year';
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
      return filterRow.subject.text + ' corporate date within current corporate year';
    }
  }
  private buildNextYearCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      return filterRow.subject.text + ' within current corporate year + 1';
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
      return filterRow.subject.text + ' corporate date within current corporate year + 1';
    }
  }
  private buildLastYearCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      return filterRow.subject.text + ' within current corporate year - 1';
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
      return filterRow.subject.text + ' corporate date within current corporate year - 1';
    }
  }
  private buildYearToDateCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      return filterRow.subject.text + ' year = current corporate year and ' + filterRow.subject.text + ' <= current corporate date';
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
      return filterRow.subject.text + ' year = current corporate year and ' + filterRow.subject.text + ' corporate date <= current corporate date';
    }
  }
  private buildMonthToDateCondition(filterRow: FilterRow): string {

    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD) {
      return filterRow.subject.text + ' period = current corporate period and ' + filterRow.subject.text + ' <= current corporate date';
    }

    if (filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
      return filterRow.subject.text + ' period = current corporate period and ' + filterRow.subject.text + ' corporate date <= current corporate date';
    }
  }
  private buildKeyFieldAgentCondition(filterRow: FilterRow) {
    let agentCondition = `(${filterRow.subject.relatedBusinessClass}).${filterRow.subject.relatedBusinessClass}`;
    switch (filterRow.option) {
      case 'actor':
        agentCondition = `actor.agent${agentCondition}`;
        break;
      case 'authenticated actor':
        agentCondition = `authenticated actor.agent${agentCondition}`;
        break;
      default:
        return '';
    }

    switch (filterRow.condition) {
      case 'equalTo':
        return filterRow.subject.text + ' = ' + agentCondition;
      case 'notEqualTo':
        return filterRow.subject.text + ' != ' + agentCondition;
      default:
        return '';
    }
  }
  public updateFilterRowConditions(filterRow: FilterRow): Array<FilterConditionOption> {
    const conditions: Array<FilterConditionOption> = [];

    if (filterRow.subject.isDimension) {
      conditions.push({ value: 'within', label: Soho.Locale.translate("FilterBuilder_Within") });
      filterRow.condition = 'within';
    } else if (filterRow.subject.type === 'DATE' || filterRow.subject.type === 'CHANGESTAMP') {
      if (filterRow.subject.fieldType === AppConstants.YYYYMMDD || filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
        conditions.push({ value: 'equalTo',               label: Soho.Locale.translate("FilterBuilder_Equal") });
        conditions.push({ value: 'notEqualTo',            label: Soho.Locale.translate("FilterBuilder_NotEqual") });
        conditions.push({ value: 'greaterThan',           label: Soho.Locale.translate("FilterBuilder_GreaterThan") });
        conditions.push({ value: 'greaterThanOrEqualTo',  label: Soho.Locale.translate("FilterBuilder_GreaterOrEqual") });
        conditions.push({ value: 'lessThan',              label: Soho.Locale.translate("FilterBuilder_LessThan") });
        conditions.push({ value: 'lessThanOrEqualTo',     label: Soho.Locale.translate("FilterBuilder_LessOrEqual") });
        conditions.push({ value: 'entered',               label: Soho.Locale.translate("FilterBuilder_Entered") });
        conditions.push({ value: 'notEntered',            label: Soho.Locale.translate("FilterBuilder_NotEntered") });
        conditions.push({ value: 'within',                label: Soho.Locale.translate("FilterBuilder_Within") });

        if (!this.hasCondition(filterRow.condition, conditions)) {
          filterRow.condition = 'within';
        }

      } else {
        conditions.push({ value: 'equalTo',               label: Soho.Locale.translate("FilterBuilder_Equal") });
        conditions.push({ value: 'notEqualTo',            label: Soho.Locale.translate("FilterBuilder_NotEqual") });
        conditions.push({ value: 'greaterThan',           label: Soho.Locale.translate("FilterBuilder_GreaterThan") });
        conditions.push({ value: 'greaterThanOrEqualTo',  label: Soho.Locale.translate("FilterBuilder_GreaterOrEqual") });
        conditions.push({ value: 'lessThan',              label: Soho.Locale.translate("FilterBuilder_LessThan") });
        conditions.push({ value: 'lessThanOrEqualTo',     label: Soho.Locale.translate("FilterBuilder_LessOrEqual") });
        conditions.push({ value: 'entered',               label: Soho.Locale.translate("FilterBuilder_Entered") });
        conditions.push({ value: 'notEntered',            label: Soho.Locale.translate("FilterBuilder_NotEntered") });

        if (!this.hasCondition(filterRow.condition, conditions)) {
          filterRow.condition = 'equalTo';
        }
      }

    } else if (filterRow.subject.type === 'TEXT'
        || filterRow.subject.type === 'GROUP'
        || filterRow.subject.type === 'BINARY'
        || filterRow.subject.type === 'BINARY_DOCUMENT'
        || filterRow.subject.type === 'BINARY_OBJECT'
        || filterRow.subject.type === 'UNIQUEID'
        || filterRow.subject.type === 'ACTOR'
        || filterRow.subject.type === 'ARRAY'
        || filterRow.subject.type === 'BUSCLASS'
        || filterRow.subject.type === 'CONTEXT') {
      conditions.push({ value: 'equalTo',     label: Soho.Locale.translate("FilterBuilder_Equal") });
      conditions.push({ value: 'notEqualTo',  label: Soho.Locale.translate("FilterBuilder_NotEqual") });
      conditions.push({ value: 'like',        label: Soho.Locale.translate("FilterBuilder_Like") });
      conditions.push({ value: 'entered',     label: Soho.Locale.translate("FilterBuilder_Entered") });
      conditions.push({ value: 'notEntered',  label: Soho.Locale.translate("FilterBuilder_NotEntered") });

      if (!this.hasCondition(filterRow.condition, conditions)) {
        filterRow.condition = 'equalTo';
      }
    } else  if (filterRow.subject.type === 'NUMBER') {
      conditions.push({ value: 'equalTo',               label: Soho.Locale.translate("FilterBuilder_Equal") });
      conditions.push({ value: 'notEqualTo',            label: Soho.Locale.translate("FilterBuilder_NotEqual") });
      conditions.push({ value: 'greaterThan',           label: Soho.Locale.translate("FilterBuilder_GreaterThan") });
      conditions.push({ value: 'greaterThanOrEqualTo',  label: Soho.Locale.translate("FilterBuilder_GreaterOrEqual") });
      conditions.push({ value: 'lessThan',              label: Soho.Locale.translate("FilterBuilder_LessThan") });
      conditions.push({ value: 'lessThanOrEqualTo',     label: Soho.Locale.translate("FilterBuilder_LessOrEqual") });
      conditions.push({ value: 'entered',               label: Soho.Locale.translate("FilterBuilder_Entered") });
      conditions.push({ value: 'notEntered',            label: Soho.Locale.translate("FilterBuilder_NotEntered") });

      if (!this.hasCondition(filterRow.condition, conditions)) {
        filterRow.condition = 'equalTo';
      }
    } else  if (filterRow.subject.type === 'BOOLEAN' || filterRow.subject.type === 'CONDITION' || filterRow.subject.type === 'STATE') {
      conditions.push({ value: 'true',        label: Soho.Locale.translate("FilterBuilder_True") });
      conditions.push({ value: 'false',       label: Soho.Locale.translate("FilterBuilder_False") });
      conditions.push({ value: 'equalTo',     label: Soho.Locale.translate("FilterBuilder_Equal") });
      conditions.push({ value: 'notEqualTo',  label: Soho.Locale.translate("FilterBuilder_NotEqual") });

      if (!this.hasCondition(filterRow.condition, conditions)) {
        filterRow.condition = 'true';
      }
    } else if (filterRow.subject.type === 'OTO_RELATION' || filterRow.subject.type === 'OTM_RELATION') {
      conditions.push({ value: 'exist',         label: Soho.Locale.translate("FilterBuilder_Exists") });
      conditions.push({ value: 'doesNotExist',  label: Soho.Locale.translate("FilterBuilder_NotExists") });

      if (!this.hasCondition(filterRow.condition, conditions)) {
        filterRow.condition = 'exist';
      }
    }

    return conditions;
  }

  private hasCondition(condition: string, conditions: Array<FilterConditionOption>): boolean {
    const foundOption = conditions.find(option => {
      return (condition === option.value);
    });

    return !!foundOption;
  }

  public updateFilterRowOptions(filterRow: FilterRow) {
    const options: Array<FilterOptionOption> = [];

    // all field types include custom = value and field = another field value
    if (filterRow.subject.isDimension) {
      options.push({ value: 'custom', label: Soho.Locale.translate("FilterBuilder_ExactValue") });
      filterRow.option = 'custom';
    } else
    if (filterRow.subject.fieldType === AppConstants.YYYYMMDD || filterRow.subject.fieldType === AppConstants.TIMESTAMP) {
      if (filterRow.condition === 'within') {
        options.push({ value: 'thisMonth',  label: Soho.Locale.translate("FilterBuilder_ThisMonth") });
        options.push({ value: 'lastMonth',  label: Soho.Locale.translate("FilterBuilder_LastMonth") });
        options.push({ value: 'nextMonth',  label: Soho.Locale.translate("FilterBuilder_NextMonth") });
        options.push({ value: 'thisYear',   label: Soho.Locale.translate("FilterBuilder_ThisYear") });
        options.push({ value: 'lastYear',   label: Soho.Locale.translate("FilterBuilder_LastYear") });
        options.push({ value: 'nextYear',   label: Soho.Locale.translate("FilterBuilder_NextYear") });
        options.push({ value: 'yearToDate',   label: Soho.Locale.translate("FilterBuilder_YTD") });
        options.push({ value: 'monthToDate',  label: Soho.Locale.translate("FilterBuilder_MTD") });

        if (!this.hasOption(filterRow, options)) {
          filterRow.option = 'thisMonth';
        }
      } else if (filterRow.condition === 'entered' || filterRow.condition === 'notEntered') {
        options.push({ value: 'custom',     label: Soho.Locale.translate("FilterBuilder_ExactValue") });
        filterRow.option = 'custom';
      } else {
        options.push({ value: 'custom',     label: Soho.Locale.translate("FilterBuilder_ExactValue") });
        options.push({ value: 'field',      label: Soho.Locale.translate("FilterBuilder_Field") });
        options.push({ value: 'today',      label: Soho.Locale.translate("FilterBuilder_Today") });
        options.push({ value: 'yesterday',  label: Soho.Locale.translate("FilterBuilder_Yesterday") });
        options.push({ value: 'tomorrow',   label: Soho.Locale.translate("FilterBuilder_Tomorrow") });

        if (!this.hasOption(filterRow, options)) {
          filterRow.option = 'today';
        }
      }
    } else if (filterRow.subject.type === 'BOOLEAN' || filterRow.subject.type === 'CONDITION') {
      options.push({ value: 'field', label: Soho.Locale.translate("FilterBuilder_Field") });

      if (!this.hasOption(filterRow, options)) {
        filterRow.option = 'field';
      }
    } else  {
      options.push({ value: 'custom',   label: Soho.Locale.translate("FilterBuilder_ExactValue") });
      options.push({ value: 'field',    label: Soho.Locale.translate("FilterBuilder_Field") });

      if (filterRow.subject.isKeyFieldAgent && (filterRow.condition === 'equalTo' || filterRow.condition === 'notEqualTo')) {
        options.push({ value: 'actor',   label: Soho.Locale.translate("FilterBuilder_Actor") } );
        options.push({ value: 'authenticated actor',   label: Soho.Locale.translate("FilterBuilder_AuthenticatedActor") });
      }

      if (!this.hasOption(filterRow, options)) {
        filterRow.option = 'custom';
      }
    }

    return options;
  }
  private hasOption(filterRow: FilterRow, options: Array<FilterOptionOption>): boolean {
    const foundOption = options.find(opt => {
      if (opt.value === filterRow.option){
        return opt;
      }
    });

    return !!foundOption;
  }
  public hasPredicate(filterRow: FilterRow) {
    if (filterRow.subject.type === 'BOOLEAN' ||  filterRow.subject.type === 'CONDITION' || filterRow.subject.type === 'STATE') {
      return (filterRow.condition === 'equalTo' || filterRow.condition === 'notEqualTo');
    }

    return !(filterRow.subject.type === 'OTO_RELATION' || filterRow.subject.type === 'OTM_RELATION' || filterRow.condition === 'entered' || filterRow.condition === 'notEntered');
  }

  public updateQualifierFromUiFieldSpec(qualifier: Qualifier, uiFieldSpec: UIFieldSpec) {
    if (uiFieldSpec) {
      qualifier.isKeyField = uiFieldSpec.isKeyField;
      qualifier.selectList = uiFieldSpec.selectList;
      qualifier.relatedBusinessClass = uiFieldSpec.relatedBusinessClass;
      qualifier.isKeyFieldAgent = uiFieldSpec.isKeyFieldAgent;
      qualifier.extendedBusinessClass = uiFieldSpec.extendedBusinessClass;
      qualifier.isStateField = uiFieldSpec.isStateField;
      qualifier.states  = uiFieldSpec.states;
      qualifier.type = uiFieldSpec.fieldType;
      qualifier.fieldType = uiFieldSpec.lmrkFieldType;
      qualifier.isDimension = uiFieldSpec.isDimension;

      if (uiFieldSpec.isDimension) {
        // qualifier.cubeDimension = this.lmCubeUtilService.convertUIFieldSpecToDimension(uiFieldSpec);
        qualifier.cubeDimension = uiFieldSpec.lmDimension;
      }
    }
  }
}
